/**
 * <AssignMembershipModal /> — shared by:
 *   - /owner/membership/members  ("Assign Membership" CTA)
 *   - /owner/crm  (customer drawer → Memberships tab assign button)
 *
 * Props:
 *   open          — boolean
 *   onClose       — () => void
 *   brandId       — string (owner brand from JWT or admin context)
 *   customerId    — string?  (CRM-context: preselects the customer; skip step 1)
 *   customerLabel — string?  (preselected customer's display label)
 *
 * Flow:
 *   Step 1: Pick customer (skipped when customerId is provided)
 *   Step 2: Pick plan (active plans only)
 *   Step 3: Optional start_date (default today)
 */
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Calendar, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import Modal from '@/components/ui_primitives/Modal';
import Button from '@/components/ui_primitives/Button';
import { Input, FieldRow, Select } from '@/components/ui_primitives/Field';
import { Spinner } from '@/components/ui_primitives/Misc';
import TierBadge from '@/components/membership/TierBadge';
import { rupiah } from '@/lib/membershipFmt';

export default function AssignMembershipModal({
  open, onClose, brandId, customerId: pickedCustomerId, customerLabel,
}) {
  const token = useAuthStore((s) => s.token);
  const qc = useQueryClient();
  const [customerId, setCustomerId] = useState(pickedCustomerId ?? null);
  const [search, setSearch] = useState('');
  const [planId, setPlanId] = useState('');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setCustomerId(pickedCustomerId ?? null);
    setSearch('');
    setPlanId('');
    setStartDate(new Date().toISOString().slice(0, 10));
  }, [open, pickedCustomerId]);

  const searchQ = useQuery({
    queryKey: ['owner-crm-search', brandId, search],
    queryFn: () => api.ownerCrmCustomers(token, { q: search, limit: 8 }),
    enabled: open && !pickedCustomerId && search.trim().length >= 2,
    staleTime: 5_000,
  });

  const plansQ = useQuery({
    queryKey: qk.ownerMembershipPlans(brandId),
    queryFn: () => api.ownerMembershipPlans(token, brandId),
    enabled: open && !!brandId,
  });

  const tiersQ = useQuery({
    queryKey: qk.ownerMembershipTiers(brandId),
    queryFn: () => api.ownerMembershipTiers(token, brandId),
    enabled: open && !!brandId,
  });

  const assignM = useMutation({
    mutationFn: ({ cid, pid, sd }) =>
      api.ownerMembershipAssign(token, brandId, {
        customer_id: cid,
        plan_id: pid,
        ...(sd ? { start_date: new Date(sd + 'T00:00:00Z').toISOString() } : {}),
      }),
    onSuccess: () => {
      toast.success('Membership assigned');
      qc.invalidateQueries({ queryKey: qk.ownerMembershipMembers(brandId) });
      qc.invalidateQueries({ queryKey: qk.ownerMembershipDashboard(brandId) });
      qc.invalidateQueries({ queryKey: qk.customerMyMemberships() });
      onClose?.();
    },
    onError: (e) => toast.error(e?.detail || e?.message || 'Could not assign membership'),
  });

  const activePlans = (plansQ.data ?? []).filter((p) => p.active);
  // Hydrate tier inline for nicer dropdown chips. Backend list_plans omits
  // the nested tier object (only tier_id) so we join client-side.
  const tierMap = Object.fromEntries((tiersQ.data ?? []).map((t) => [t.id, t]));
  const hydratedPlans = activePlans.map((p) => ({
    ...p,
    tier: p.tier || tierMap[p.tier_id] || null,
  }));
  const submitDisabled = !customerId || !planId || assignM.isPending;
  const customers = (searchQ.data?.items ?? searchQ.data ?? []);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Assign Membership"
      subtitle="Assigns the chosen plan to the customer for this brand. Any existing active membership for this customer & brand will be transparently cancelled."
      size="md"
      testID="assign-membership-modal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={assignM.isPending}
            testID="assign-cancel">
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={assignM.isPending}
            disabled={submitDisabled}
            onClick={() => assignM.mutate({ cid: customerId, pid: planId, sd: startDate })}
            testID="assign-submit"
          >
            Assign
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Step 1: customer */}
        <FieldRow label="Customer" required>
          {pickedCustomerId ? (
            <div
              data-testid="assign-customer-prefilled"
              className="rounded-2xl border border-gold/25 bg-gold/5 px-4 py-3 text-sm"
            >
              {customerLabel ?? pickedCustomerId}
            </div>
          ) : (
            <>
              <div className="relative">
                <Search size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <Input
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    // User is editing the search — clear any prior pick so the
                    // dropdown re-opens and they can choose a different customer.
                    if (customerId) setCustomerId(null);
                  }}
                  placeholder="Search by name, email or phone…"
                  className="pl-9"
                  data-testid="assign-customer-search"
                />
              </div>
              {searchQ.isFetching ? (
                <div className="mt-2 text-xs text-white/45 inline-flex items-center gap-2">
                  <Loader2 size={12} className="animate-spin" /> Searching…
                </div>
              ) : null}
              {search.trim().length >= 2 && customers.length > 0 && !customerId ? (
                <div className="mt-2 max-h-44 overflow-y-auto rounded-xl border border-white/8 bg-black/30 divide-y divide-white/5"
                  data-testid="assign-customer-results">
                  {customers.map((c) => {
                    // Owner CRM search payload exposes the customer's user-id as
                    // `customer_id` (not `id`). Fall back to `id` for safety in
                    // case the shape ever normalizes.
                    const cid = c.customer_id ?? c.id;
                    return (
                    <button
                      key={cid}
                      type="button"
                      onClick={() => { setCustomerId(cid); setSearch(c.name || c.email || c.phone || cid); }}
                      data-testid={`assign-customer-pick-${cid}`}
                      className={
                        'w-full text-left px-3 py-2.5 hover:bg-gold/10 transition-colors ' +
                        (customerId === cid ? 'bg-gold/15 text-gold' : 'text-white/85')
                      }
                    >
                      <div className="text-sm font-medium truncate">{c.name || '—'}</div>
                      <div className="text-[11px] text-white/45 truncate">
                        {c.email || c.phone || c.membership_id || cid}
                      </div>
                    </button>
                    );
                  })}
                </div>
              ) : null}
              {customerId && search.trim().length < 2 ? (
                <div className="mt-2 text-xs text-gold" data-testid="assign-customer-picked">
                  Picked: {customerId}
                </div>
              ) : null}
            </>
          )}
        </FieldRow>

        {/* Step 2: plan */}
        <FieldRow label="Plan" required>
          {plansQ.isLoading ? <Spinner /> : (
            <Select
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              data-testid="assign-plan-select"
            >
              <option value="">— Select a plan —</option>
              {hydratedPlans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {p.is_lifetime ? 'Lifetime' : `${p.duration_days}d`} · {rupiah(p.price)}
                </option>
              ))}
            </Select>
          )}
          {planId && (() => {
            const p = hydratedPlans.find((x) => x.id === planId);
            return p ? (
              <div className="mt-2 inline-flex items-center gap-2 text-xs text-white/55"
                data-testid="assign-plan-summary">
                {p.tier ? <TierBadge tier={p.tier} /> : null}
                <span>{rupiah(p.price)} · {p.is_lifetime ? 'Lifetime' : `${p.duration_days} days`}</span>
              </div>
            ) : null;
          })()}
        </FieldRow>

        {/* Step 3: start_date */}
        <FieldRow label="Start date" hint="Optional (defaults to today)">
          <div className="relative">
            <Calendar size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="pl-9"
              data-testid="assign-start-date"
            />
          </div>
        </FieldRow>
      </div>
    </Modal>
  );
}
